import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Badge,
  Divider,
  Spinner,
  Center,
  Box,
} from "@chakra-ui/react";
import { MdSync } from "react-icons/md";
import { FaDownload } from "react-icons/fa";

interface RefundDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticketId: string;
  refunds: any[];
  isLoading: boolean;
  isSyncing: boolean;
  onSync: (refundId: string) => Promise<void>;
  onDownloadCreditNote?: (refundId: string) => Promise<void>;
  isDownloadingCreditNote?: boolean;
}

export const RefundDetailsModal: React.FC<RefundDetailsModalProps> = ({
  isOpen,
  onClose,
  ticketId,
  refunds,
  isLoading,
  isSyncing,
  onSync,
  onDownloadCreditNote,
  isDownloadingCreditNote,
}) => {
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "processed":
        return <Badge colorScheme="green">PROCESSED</Badge>;
      case "pending":
        return <Badge colorScheme="yellow">PENDING</Badge>;
      case "failed":
        return <Badge colorScheme="red">FAILED</Badge>;
      default:
        return <Badge colorScheme="gray">{status?.toUpperCase() || "UNKNOWN"}</Badge>;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack justify="space-between" pr={6}>
            <Text>Refund Details</Text>
            <Badge colorScheme="blue">Ticket: {ticketId || "N/A"}</Badge>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isLoading ? (
            <Center py={8}>
              <Spinner size="lg" color="blue.500" />
            </Center>
          ) : !refunds || refunds.length === 0 ? (
            <Center py={8}>
              <Text color="gray.500">No refund records found for this ticket.</Text>
            </Center>
          ) : (
            <VStack spacing={4} align="stretch">
              {refunds.map((refund, idx) => (
                <Box
                  key={refund._id || idx}
                  p={4}
                  borderWidth="1px"
                  borderRadius="md"
                  bg={refund.refund_status === "processed" ? "green.50" : refund.refund_status === "failed" ? "red.50" : "gray.50"}
                >
                  <HStack justify="space-between" mb={2}>
                    <HStack>
                      <Text fontWeight="bold" fontSize="md">₹{refund.amount ?? refund.refund_amount ?? 0}</Text>
                      {getStatusBadge(refund.refund_status)}
                    </HStack>
                    <HStack>
                      {refund.refund_status === "processed" && onDownloadCreditNote && (
                        <Button
                          size="xs"
                          leftIcon={<FaDownload />}
                          colorScheme="teal"
                          variant="outline"
                          isLoading={isDownloadingCreditNote}
                          onClick={() => onDownloadCreditNote(refund._id)}
                        >
                          Credit Note
                        </Button>
                      )}
                      <Button
                        size="xs"
                        leftIcon={<MdSync />}
                        colorScheme="blue"
                        variant="outline"
                        isLoading={isSyncing}
                        onClick={() => onSync(refund._id)}
                      >
                        Sync Status
                      </Button>
                    </HStack>
                  </HStack>

                  <Divider my={2} />

                  <VStack align="stretch" spacing={1} fontSize="xs" color="gray.600">
                    <HStack justify="space-between">
                      <Text>Razorpay Refund ID:</Text>
                      <Text fontFamily="mono" fontWeight="medium" color="gray.800">{refund.provider_refund_id || "N/A"}</Text>
                    </HStack>

                    <HStack justify="space-between">
                      <Text>Reason:</Text>
                      <Text>{refund.reason || "N/A"}</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text>Initiated By:</Text>
                      <Text>{refund.initiated_by || "System"}</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text>Initiated At:</Text>
                      <Text>{new Date(refund.createdAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</Text>
                    </HStack>
                    {refund.failure_reason && (
                      <HStack justify="space-between" color="red.600">
                        <Text>Failure Reason:</Text>
                        <Text>{refund.failure_reason}</Text>
                      </HStack>
                    )}
                  </VStack>
                </Box>
              ))}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
